import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Time } from '@/components/ui/time';

export const Profile: React.FC = () => {
  const { user, isLoading, isAuthenticated } = useAuth0();

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto pt-5 pb-8">
          <div className="h-10 w-40 bg-foreground/5 rounded animate-pulse mb-8" />
          <div className="space-y-12">
            <div className="h-24 bg-foreground/5 rounded animate-pulse" />
            <div className="h-40 bg-foreground/5 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto pt-5 pb-8">
          <h2 className="text-2xl font-medium mb-2">Access Denied</h2>
          <p className="text-sm text-muted-foreground">Please log in to view your profile.</p>
        </div>
      </div>
    );
  }

  const avatarFallback =
    user?.name?.charAt(0).toUpperCase() ||
    user?.email?.charAt(0).toUpperCase() ||
    'U';

  const auth0ProfileUrl = `https://${import.meta.env.VITE_AUTH0_DOMAIN}/u/profile`;

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto pt-5 pb-8">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Profile</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Your account details, synced from Auth0.
          </p>
        </div>

        <div className="space-y-12">
          {/* Identity */}
          <section className="flex gap-8">
            <div className="w-48 flex-shrink-0">
              <h2 className="text-xl font-medium text-foreground">Identity</h2>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16 flex-shrink-0">
                  <AvatarImage src={user?.picture} alt={user?.name || user?.email || 'User'} />
                  <AvatarFallback className="bg-surface text-foreground text-base">
                    {avatarFallback}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-medium text-foreground truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Account */}
          <section className="flex gap-8">
            <div className="w-48 flex-shrink-0">
              <h2 className="text-xl font-medium text-foreground">Account</h2>
            </div>
            <div className="flex-1 min-w-0">
              <dl className="space-y-4">
                <div className="flex items-baseline">
                  <dt className="text-sm font-medium text-muted-foreground w-32 flex-shrink-0">Full name</dt>
                  <dd className="text-sm text-foreground">{user?.name || 'Not provided'}</dd>
                </div>
                <div className="flex items-baseline">
                  <dt className="text-sm font-medium text-muted-foreground w-32 flex-shrink-0">Email</dt>
                  <dd className="text-sm text-foreground break-all">{user?.email}</dd>
                </div>
                <div className="flex items-center">
                  <dt className="text-sm font-medium text-muted-foreground w-32 flex-shrink-0">Email verified</dt>
                  <dd>
                    <Badge variant={user?.email_verified ? 'success' : 'destructive'}>
                      {user?.email_verified ? 'Verified' : 'Unverified'}
                    </Badge>
                  </dd>
                </div>
                {user?.updated_at && (
                  <div className="flex items-baseline">
                    <dt className="text-sm font-medium text-muted-foreground w-32 flex-shrink-0">Last updated</dt>
                    <dd className="text-sm text-foreground"><Time value={user.updated_at} format="short" /></dd>
                  </div>
                )}
                <div className="flex items-baseline">
                  <dt className="text-sm font-medium text-muted-foreground w-32 flex-shrink-0">User ID</dt>
                  <dd className="text-sm text-foreground font-mono break-all">{user?.sub}</dd>
                </div>
              </dl>
            </div>
          </section>

          {/* Manage */}
          <section className="flex gap-8">
            <div className="w-48 flex-shrink-0">
              <h2 className="text-xl font-medium text-foreground">Manage</h2>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground mb-2">
                Profile editing and password changes happen in your Auth0 account.
              </p>
              <a
                href={auth0ProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-foreground hover:underline"
              >
                Manage profile ↗
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Profile;
